// The `PopularCategorySection` component is a React functional component that displays a horizontally
// scrollable list of popular categories using a carousel layout. It dynamically adjusts the number
// of slides visible based on the screen size and provides navigation buttons for scrolling left or right.
// Disabled states and smooth scrolling enhance the user experience, making it suitable for showcasing multiple
// items interactively.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop, tablet } from 'helpers/utils/screensizes';
import Wrapper from 'components/HOC/wrapper';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Carousel, CarouselContentItem } from 'types/next';
import CategoryTile from './tile';
import useLocale from 'helpers/hooks/useLocale';

export type CategoryData = {
  content: Carousel | undefined;
};

const PopularCategorySection = ({ content }: CategoryData) => {
  const { isTouchDevice } = useTouchDevice();
  const contentItems = (content?.contentItems as CarouselContentItem[]) || [];
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);

  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';

  const slidesPerView = useMemo(() => {
    if (isMediumDesktopSize) return content?.slidesPerView?.largeDesktop || 5.5;
    if (isDesktopSize) return content?.slidesPerView?.desktop || 4.5;
    if (isTablet) return content?.slidesPerView?.tablet || 3.5;
    return content?.slidesPerView?.mobile || 2.5;
  }, [isMediumDesktopSize, isDesktopSize, isTablet]);

  // Update slide width on mount & when container size changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      setSlideWidth(scrollContainerRef.current.offsetWidth / slidesPerView);
    }
  }, [slidesPerView]);

  // Scroll event handler to track scrollbar position
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

    setIsScrolledToStart(isAtStart);
    setIsScrolledToEnd(isAtEnd);

    const newStartIndex = Math.round(scrollLeft / slideWidth);
    setStartIndex(newStartIndex);
  };

  // Scroll Left with Smooth Behavior
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Scroll Right with Smooth Behavior
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Attach scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Run initially to set correct button states
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [contentItems?.length, slidesPerView, slideWidth]);

  return (
    <Wrapper clearDefaultStyles className="homepage-carousels-section pb-16 pl-16">
      <div className={`mb-16 flex items-center ${isDesktopSize ? 'relative justify-center' : 'justify-between pr-10'}`}>
        <div className="align-center flex justify-center text-center">
          <h2 className="text-24 font-bold leading-9 sm:text-36">{content?.heading?.[selectedLocale] ?? ''}</h2>
        </div>
        <div className={`flex items-center justify-end gap-x-6 ${isDesktopSize ? 'absolute right-[1rem]' : ''}`}>
          <button
            className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0] 
    ${isScrolledToStart ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={handleScrollLeft}
            disabled={isScrolledToStart}
            aria-disabled={isScrolledToStart}
          >
            <ChevronLeftIcon
              className="mt-2 h-20 w-20 font-bold text-[#404040]"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
            />
          </button>

          <button
            className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0] 
    ${isScrolledToEnd ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={handleScrollRight}
            disabled={isScrolledToEnd}
            aria-disabled={isScrolledToEnd}
          >
            <ChevronRightIcon
              className="mt-2 h-20 w-20 font-bold text-[#404040]"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
            />
          </button>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className="ag__cc__wrapper flex"
        style={{
          WebkitOverflowScrolling: 'touch',
          paddingBottom: '10px', // reserve scrollbar height
        }}
      >
        {contentItems?.map((data, index) => (
          <div
            key={index}
            style={{
              flex: `0 0 calc(100% / ${slidesPerView})`,
              scrollSnapAlign: 'start',
            }}
          >
            <CategoryTile data={data} locale={selectedLocale} />
          </div>
        ))}
      </div>
    </Wrapper>
  );
};

export default PopularCategorySection;
